import { addDays, format, isValid, parseISO } from 'date-fns';
export const todayKey = () => format(new Date(), 'yyyy-MM-dd');
export const dayKey = value => { const date = value instanceof Date ? value : parseISO(value); return isValid(date) ? format(date, 'yyyy-MM-dd') : null; };
export const shiftDay = (day, days) => format(addDays(parseISO(day), days), 'yyyy-MM-dd');
export const validDay = day => typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day) && dayKey(day) === day;
const numeric = value => value !== '' && value != null && Number.isFinite(Number(value));
const round = weight => Math.round((weight + Number.EPSILON) * 100) / 100;
export const WEIGHT_STEP = 2.5;
export const MODE_LABELS = { 'percent-base': '% del peso inicial', 'percent-previous': '% sobre etapa anterior', weight: 'Peso exacto (kg)' };

export function resolvePlan(input) {
  if (!input.exerciseId || !validDay(input.startDate) || !numeric(input.baseWeight) || Number(input.baseWeight) < 0) throw new Error('Selecciona un ejercicio, una fecha válida y un peso inicial mayor o igual a cero.');
  const baseWeight = Number(input.baseWeight);
  let previousWeight = baseWeight;
  let previousDate = input.startDate;
  const ids = new Set();
  const steps = [...input.steps].sort((a, b) => a.date.localeCompare(b.date)).map(step => {
    if (!step.id || ids.has(step.id)) throw new Error('Hay etapas duplicadas.');
    ids.add(step.id);
    if (!validDay(step.date) || step.date <= previousDate) throw new Error('Cada etapa debe tener una fecha distinta, posterior al inicio.');
    if (!Object.prototype.hasOwnProperty.call(MODE_LABELS, step.mode) || !numeric(step.value)) throw new Error('Introduce un ajuste válido para cada etapa.');
    const value = Number(step.value);
    const reference = step.mode === 'percent-base' ? baseWeight : previousWeight;
    if (step.mode !== 'weight' && (value <= -100 || reference <= 0)) throw new Error('El porcentaje debe ser mayor que −100% y partir de un peso mayor que cero.');
    const requested = step.mode === 'weight' ? value : reference * (1 + value / 100);
    const increments = (requested - previousWeight) / WEIGHT_STEP;
    if (step.mode === 'weight' && Math.abs(increments - Math.round(increments)) > 1e-8) throw new Error('El peso exacto debe cambiar en múltiplos de 2,5 kg respecto a la etapa anterior.');
    const roundedIncrements = Math.abs(increments - Math.round(increments)) < 1e-8 ? Math.round(increments)
      : increments > 0 ? Math.ceil(increments) : Math.floor(increments);
    const weight = previousWeight + roundedIncrements * WEIGHT_STEP;
    if (!Number.isFinite(weight) || weight < 0) throw new Error('La carga calculada debe ser un número mayor o igual a cero.');
    previousWeight = round(weight);
    previousDate = step.date;
    return { ...step, value, targetWeight: previousWeight, requestedWeight: round(requested) };
  });
  return { ...input, baseWeight, steps };
}

export function validatePlanChange(input, previous, today = todayKey()) {
  const plan = resolvePlan(input);
  if (!previous) {
    if (plan.startDate < today) throw new Error('Un nuevo plan debe comenzar hoy o en una fecha futura.');
    return plan;
  }
  if (plan.exerciseId !== previous.exerciseId) throw new Error('No puedes cambiar el ejercicio de un plan existente.');
  if (previous.startDate <= today && (plan.startDate !== previous.startDate || plan.baseWeight !== previous.baseWeight)) throw new Error('El peso y la fecha iniciales ya están fijados. Añade una etapa futura para ajustarlos.');
  if (previous.startDate > today && plan.startDate < today) throw new Error('El inicio no puede trasladarse al pasado.');
  for (const old of previous.steps.filter(step => step.date <= today)) {
    const next = plan.steps.find(step => step.id === old.id);
    if (!next || ['date', 'mode', 'value', 'targetWeight'].some(key => next[key] !== old[key])) throw new Error('Las etapas pasadas y la de hoy están fijadas; modifica solo las futuras.');
  }
  for (const step of plan.steps.filter(step => step.date <= today)) {
    if (!previous.steps.some(old => old.id === step.id && old.date <= today)) throw new Error('No se pueden añadir etapas retroactivas.');
  }
  return plan;
}

// A point represents the maximum weight actually completed that day, never a planned/uncompleted set.
export function actualLoads(history, exerciseId) {
  const days = new Map();
  for (const workout of history) {
    const date = dayKey(workout.date);
    if (!date) continue;
    for (const exercise of workout.exercises || []) {
      if (exercise.exerciseId !== exerciseId) continue;
      const sets = (exercise.sets || []).filter(set => set.completed === true && numeric(set.weight) && Number(set.weight) >= 0 && numeric(set.reps) && Number(set.reps) > 0);
      if (!sets.length) continue;
      const row = days.get(date) || { date, actual: 0, completedSets: 0, sessions: new Set() };
      row.actual = Math.max(row.actual, ...sets.map(set => Number(set.weight)));
      row.completedSets += sets.length;
      row.sessions.add(workout.id);
      days.set(date, row);
    }
  }
  return [...days.values()].sort((a, b) => a.date.localeCompare(b.date)).map(({ sessions, ...row }) => ({ ...row, sessions: sessions.size }));
}
export function targetAt(plan, date) {
  if (!plan || date < plan.startDate) return null;
  return [...plan.steps].reverse().find(step => step.date <= date)?.targetWeight ?? plan.baseWeight;
}
export function progressionChart(plan, history, exerciseId, today = todayKey()) {
  const actual = actualLoads(history, exerciseId);
  const byDate = new Map(actual.map(row => [row.date, row]));
  const dates = new Set(actual.map(row => row.date));
  if (plan) {
    dates.add(plan.startDate);
    plan.steps.forEach(step => dates.add(step.date));
    if (today >= plan.startDate) dates.add(today);
    const last = [...dates].sort().at(-1);
    dates.add(shiftDay(last, 14));
  }
  return [...dates].sort().map(date => ({ date, timestamp: parseISO(date).getTime(), planned: targetAt(plan, date), actual: byDate.get(date)?.actual ?? null, completedSets: byDate.get(date)?.completedSets || 0, sessions: byDate.get(date)?.sessions || 0 }));
}

export function movePlannedLoad(plan, stepId, requested, today = todayKey()) {
  const resolved = resolvePlan(plan);
  const step = resolved.steps.find(row => row.id === stepId);
  if (!step || step.date <= today || !Number.isFinite(requested)) throw new Error('Solo puedes arrastrar etapas futuras.');
  const minimum = ((resolved.baseWeight % WEIGHT_STEP) + WEIGHT_STEP) % WEIGHT_STEP;
  const value = round(minimum + Math.max(0, Math.round((requested - minimum) / WEIGHT_STEP)) * WEIGHT_STEP);
  return resolvePlan({ ...resolved, steps: resolved.steps.map(row => row.id === stepId ? { ...row, mode: 'weight', value } : row) });
}

export function buildBulkIncrease(exercises, history, plans, percentage, date, today = todayKey()) {
  if (!numeric(percentage) || Number(percentage) <= 0) throw new Error('Introduce un porcentaje mayor que cero.');
  if (!validDay(date) || date <= today) throw new Error('Selecciona una fecha posterior a hoy.');
  const changes = [], skipped = [];
  for (const exercise of exercises) {
    const existing = plans.find(plan => plan.exerciseId === exercise.id);
    if (existing && existing.startDate > date) { skipped.push({ name: exercise.name, reason: 'El plan comienza después de esta fecha.' }); continue; }
    const latest = actualLoads(history, exercise.id).filter(row => row.date <= today).slice(-1)[0];
    const baseline = latest?.actual ?? Number(exercise.defaultWeight || 0);
    const plan = existing || { exerciseId: exercise.id, exerciseName: exercise.name, startDate: today, baseWeight: baseline, steps: [] };
    const current = existing ? targetAt(plan, date) : baseline;
    if (!Number.isFinite(current) || current <= 0) { skipped.push({ name: exercise.name, reason: 'Sin carga positiva; define primero su peso inicial.' }); continue; }
    const requested = current * (1 + Number(percentage) / 100);
    const weight = round(current + Math.ceil((requested - current) / WEIGHT_STEP - 1e-10) * WEIGHT_STEP);
    const sameDate = plan.steps.find(step => step.date === date);
    const next = validatePlanChange({ ...plan, steps: [...plan.steps.filter(step => step.date !== date), {
      id: sameDate?.id || `bulk-${exercise.id}-${date}`, date, mode: 'weight', value: weight,
    }] }, existing, today);
    changes.push({ plan: next, name: exercise.name, before: current, after: targetAt(next, date) });
  }
  return { changes, skipped };
}
